// Orden de pedido de personal extra: cálculos puros (sin React), compartidos
// por la pestaña de la orden (components/OrdenPersonalExtra.js), la ficha y la
// tarjeta "Coste interno de personal" de Logística, para que la cifra sea la
// misma al céntimo en todas. Se pueden probar con node (scripts/).

export const TARIFA_POR_DEFECTO = 25;

// ---------------------------------------------------------------------------
// Cálculo (réplica exacta de `calcular_coste_orden_personal` del backend)
// ---------------------------------------------------------------------------

/**
 * Tramos horarios de un grupo: `tramos: [{inicio, fin, fecha?}]` (la misma
 * persona en varios tramos, p. ej. monta, se va y vuelve a recoger) o, en
 * órdenes antiguas, el único tramo hora_inicio - hora_fin.
 */
export const tramosDeGrupo = (grupo) => {
  const tramos = (Array.isArray(grupo?.tramos) ? grupo.tramos : []).filter(t => t && t.inicio && t.fin);
  if (tramos.length > 0) return tramos;
  if (grupo?.hora_inicio && grupo?.hora_fin) return [{ inicio: grupo.hora_inicio, fin: grupo.hora_fin }];
  return [];
};

/** Horas de UNA persona del grupo: `horas` explícitas (> 0) o la suma de sus tramos. */
export const horasDeGrupo = (grupo) => {
  const horas = parseFloat(grupo?.horas);
  if (!isNaN(horas) && horas > 0) return horas;
  return tramosDeGrupo(grupo).reduce((s, t) => s + horasEntre(t.inicio, t.fin), 0);
};

/** Horas de "HH:MM" a "HH:MM"; si el fin es menor o igual, cruza medianoche. */
const horasEntre = (inicio, fin) => {
  if (!inicio || !fin) return 0;

  const [hI, mI] = String(inicio).split(':');
  const [hF, mF] = String(fin).split(':');
  const minutosInicio = parseInt(hI, 10) * 60 + parseInt(mI, 10);
  let minutosFin = parseInt(hF, 10) * 60 + parseInt(mF, 10);
  if (isNaN(minutosInicio) || isNaN(minutosFin)) return 0;
  // Si el fin es menor o igual al inicio se asume que cruza medianoche
  if (minutosFin <= minutosInicio) minutosFin += 24 * 60;
  return (minutosFin - minutosInicio) / 60;
};

export const redondear2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;

/**
 * Re-deriva los totales de una orden desde sus grupos. No confía en los
 * snapshots `total_*` guardados (pueden estar desfasados).
 * Devuelve { total_personas, total_horas, coste_total }.
 */
export const calcularCosteOrdenPersonal = (orden) => {
  const vacio = { total_personas: 0, total_horas: 0, coste_total: 0 };
  if (!orden || typeof orden !== 'object') return vacio;

  // Tarifa: 0 es un valor válido; solo se cae al defecto si falta o no es numérica.
  const tarifaRaw = orden.tarifa_hora;
  let tarifaHora = (tarifaRaw === null || tarifaRaw === undefined) ? TARIFA_POR_DEFECTO : parseFloat(tarifaRaw);
  if (isNaN(tarifaHora)) tarifaHora = TARIFA_POR_DEFECTO;

  let totalPersonas = 0;
  let totalHoras = 0;
  let totalCoste = 0;
  (Array.isArray(orden.grupos) ? orden.grupos : []).forEach((grupo) => {
    if (!grupo || typeof grupo !== 'object') return;
    const numPersonas = parseInt(grupo.num_personas, 10);
    if (isNaN(numPersonas) || numPersonas <= 0) return;
    const horasGrupo = numPersonas * horasDeGrupo(grupo);
    totalPersonas += numPersonas;
    totalHoras += horasGrupo;
    // Tarifa propia del grupo (grupos de órdenes antiguas por jornada con otra tarifa)
    const tarifaGrupo = parseFloat(grupo.tarifa_hora);
    totalCoste += horasGrupo * (grupo.tarifa_hora !== null && grupo.tarifa_hora !== undefined && grupo.tarifa_hora !== ''
      && !isNaN(tarifaGrupo) ? tarifaGrupo : tarifaHora);
  });

  return {
    total_personas: totalPersonas,
    total_horas: redondear2(totalHoras),
    // Igual que el backend: el coste se calcula sobre las horas SIN redondear
    coste_total: redondear2(totalCoste),
  };
};

// ---------------------------------------------------------------------------
// Multievento: una sola orden para todo el evento (réplica de backend/utils.py)
// ---------------------------------------------------------------------------

export const esMultievento = (presupuesto) => !!presupuesto?.es_multi_evento
  && Array.isArray(presupuesto?.jornadas) && presupuesto.jornadas.length > 0;

export const fecha10 = (f) => (f ? String(f).slice(0, 10) : null);

/** Jornada de un grupo: su `jornada_id`; si no, la de su `fecha`; si tampoco, la primera. */
export const jornadaDeGrupo = (presupuesto, grupo) => {
  const jornadas = (presupuesto?.jornadas || []).filter(j => j && typeof j === 'object');
  const ids = jornadas.map(j => j.id);
  if (grupo?.jornada_id && ids.includes(grupo.jornada_id)) return grupo.jornada_id;
  const fecha = fecha10(grupo?.fecha);
  if (fecha) {
    const j = jornadas.find(x => fecha10(x.fecha) === fecha);
    if (j) return j.id;
  }
  return ids[0] || null;
};

/** La parte de la orden de una jornada (la orden única o, sin ella, la antigua de la jornada). */
export const ordenDeJornada = (presupuesto, jornada) => {
  const raiz = presupuesto?.orden_personal_extra;
  if (raiz && typeof raiz === 'object') {
    const grupos = (raiz.grupos || []).filter(g => g && typeof g === 'object' && jornadaDeGrupo(presupuesto, g) === jornada?.id);
    if (grupos.length === 0) return null;
    return { ...raiz, grupos };
  }
  return jornada?.orden_personal_extra || null;
};

// ---------------------------------------------------------------------------
// Resumen para el coste interno de personal (réplica de la regla del motor de
// costes, backend/costes_presupuesto.py): la orden manda en el coste en cuanto
// tiene personas, salvo que esté anulada o cancelada (también en borrador)
// ---------------------------------------------------------------------------

export const ESTADOS_ORDEN_NO_VIGENTE = ['anulada', 'cancelada'];

const TEXTO_ESTADO_ORDEN = {
  borrador: 'borrador, sin enviar',
  enviada: 'enviada',
  confirmada: 'confirmada',
};

const estadoDeOrden = (orden) => String(orden?.estado || 'borrador').toLowerCase();

/**
 * Resumen de la orden de pedido que manda en el coste de personal, o null si
 * no hay orden vigente con personas.
 * - Un día: `presupuesto.orden_personal_extra`.
 * - Multievento con `jornada`: su parte de la orden (`ordenDeJornada`).
 * - Multievento sin jornada ("Todas"): la orden única de la raíz o, si aún no
 *   la hay, la suma de las órdenes antiguas de cada jornada.
 * Devuelve { personas, horas, coste, estado, textoEstado }.
 */
export const resumenOrdenPersonal = (presupuesto, jornada = null) => {
  const multi = esMultievento(presupuesto);
  const resumenDe = (orden) => {
    if (!orden || typeof orden !== 'object') return null;
    const estado = estadoDeOrden(orden);
    if (ESTADOS_ORDEN_NO_VIGENTE.includes(estado)) return null;
    const c = calcularCosteOrdenPersonal(orden);
    if (c.total_personas <= 0) return null;
    return {
      personas: c.total_personas, horas: c.total_horas, coste: c.coste_total,
      estado, textoEstado: TEXTO_ESTADO_ORDEN[estado] || estado,
    };
  };
  if (multi && jornada) return resumenDe(ordenDeJornada(presupuesto, jornada));
  const raiz = presupuesto?.orden_personal_extra;
  if (!multi || (raiz && typeof raiz === 'object')) return resumenDe(raiz);
  // Multievento sin orden en la raíz: las órdenes antiguas de cada jornada
  const partes = (presupuesto.jornadas || []).map(j => resumenDe(j?.orden_personal_extra)).filter(Boolean);
  if (partes.length === 0) return null;
  const estados = [...new Set(partes.map(x => x.estado))];
  const estado = estados.length === 1 ? estados[0] : 'borrador';
  return {
    personas: partes.reduce((a, x) => a + x.personas, 0),
    horas: redondear2(partes.reduce((a, x) => a + x.horas, 0)),
    coste: redondear2(partes.reduce((a, x) => a + x.coste, 0)),
    estado,
    textoEstado: estados.length === 1 ? (TEXTO_ESTADO_ORDEN[estado] || estado) : 'varios estados',
  };
};
